import React, { useEffect, useState } from "react";
import axios from "axios";
import apiConfig from "../../apiConfig";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Profile = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${apiConfig.baseUrl}${apiConfig.getAllUsers}`
        );
        setUsers(response.data);
        setFilteredUsers(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleDeleteUser = async (userId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this user?"
    );
    if (confirmDelete) {
      try {
        await axios.delete(
          `${apiConfig.baseUrl}${apiConfig.deleteUser}${userId}`
        );
        const updatedUsers = users.filter((user) => user._id !== userId);
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };

  const handleFilter = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    const filtered = users.filter((user) => {
      const created = new Date(user.createdAt);
      return created >= startDate && created <= endDate;
    });

    setFilteredUsers(filtered);
  };

  const resetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setFilteredUsers(users);
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!filteredUsers.length) {
    return <p>No user data available</p>;
  }

  const exportToExcel = () => {
    if (!users.length) {
      alert("No user data to export");
      return;
    }

    // Prepare data for excel (flatten user data)
    const exportData = users.map((user) => {
      return {
        ID: user._id,
        Name: user.name,
        Email: user.email,
        PhoneNumber: user.phoneNo || "N/A",
        Role: user.role,
        CreatedAt: new Date(user.createdAt).toLocaleDateString(),
        Addresses:
          user.addresses && user.addresses.length > 0
            ? user.addresses
                .map(
                  (a) =>
                    `${a.location}, ${a.city}, ${a.state}, ${a.zip} (${a.type})`
                )
                .join(" | ")
            : "No addresses found",
      };
    });

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Save to file
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "UsersData.xlsx");
  };

  return (
    <div>
      <h1>Users</h1>

      {/* Date filter */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
          padding: "15px",
          border: "1px solid #ccc",
          borderRadius: "10px",
          backgroundColor: "#f9f9f9",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
          flexWrap: "wrap",
          width: "800px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <label style={{ fontWeight: "bold", marginBottom: "5px" }}>
            From:
          </label>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="Start Date"
            style={{
              padding: "6px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <label style={{ fontWeight: "bold", marginBottom: "5px" }}>To:</label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="End Date"
            style={{
              padding: "6px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <button
          onClick={handleFilter}
          style={{
            backgroundColor: "#4CAF50",
            color: "white",
            border: "none",
            padding: "10px 16px",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Apply
        </button>
        <button
          onClick={resetFilter}
          style={{
            backgroundColor: "#f44336",
            color: "white",
            border: "none",
            padding: "10px 16px",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Reset
        </button>
      </div>

      <button
        onClick={exportToExcel}
        style={{
          backgroundColor: "#1976d2",
          color: "white",
          border: "none",
          padding: "10px 16px",
          borderRadius: "6px",
          cursor: "pointer",
          marginBottom: "15px",
        }}
      >
        Export All Users to Excel
      </button>

      {/* Table */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #ddd",
        }}
      >
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Role</th>
            <th>Created At</th>
            <th>Address</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user, index) => (
            <tr key={index}>
              <td>{user._id}</td>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.phoneNo || "N/A"}</td>
              <td>{user.role}</td>
              <td>{new Date(user.createdAt).toLocaleDateString()}</td>
              <td>
                {user.addresses && user.addresses.length > 0 ? (
                  user.addresses.map((address, addressIndex) => (
                    <div key={addressIndex}>
                      {address.location}, {address.city}, {address.state},{" "}
                      {address.zip} ({address.type})
                    </div>
                  ))
                ) : (
                  <div>No addresses found</div>
                )}
              </td>
              <td>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDeleteUser(user._id)}
                >
                  Delete User
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Profile;
